import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge } from '../atoms';

/**
 * ChainVerificationBadge — renders the trust state of the append-only audit
 * ledger on the surface (Populus agent-native UI standard): show the chain is
 * cryptographically verified, not just asserted.
 *
 * Reads GET /api/audit-logs/verify → { verified, count, head_hash,
 * first_broken_id } (contract point BE-1) and drives the badge from that live,
 * manager-gated hash-chain recompute. Loading / error states are honest.
 *
 * Domain 02.3 — Data Integrity & Audit.
 */

interface ChainVerifyResult {
  verified: boolean;
  count: number;
  head_hash: string | null;
  first_broken_id: string | null;
}

export function ChainVerificationBadge() {
  const query = useQuery<ChainVerifyResult>({
    queryKey: ['audit-logs-verify'],
    queryFn: ({ signal }) => api.get<ChainVerifyResult>('/api/audit-logs/verify', signal),
    staleTime: 60_000,
  });

  const strip = (
    accent: string,
    bg: string,
    badge: React.ReactNode,
    message: React.ReactNode,
  ) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-3) var(--sp-4)',
      background: bg,
      border: `1px solid ${accent}`,
      borderRadius: 'var(--r-lg)',
      marginBottom: 'var(--sp-4)',
    }}>
      {badge}
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>{message}</span>
    </div>
  );

  if (query.isLoading) {
    return strip(
      'var(--color-border)',
      'var(--color-card)',
      <Badge variant="neutral" dot>Verifying</Badge>,
      'Checking audit-chain integrity…',
    );
  }

  if (query.error) {
    return strip(
      'var(--color-status-red)',
      'var(--color-status-red-bg)',
      <Badge variant="danger" dot>Error</Badge>,
      `Could not verify the audit chain. ${(query.error as Error).message}`,
    );
  }

  const data = query.data!;

  if (!data.verified) {
    return strip(
      'var(--color-status-red)',
      'var(--color-status-red-bg)',
      <Badge variant="danger" dot>Chain integrity error</Badge>,
      <>
        The audit chain is broken{data.first_broken_id ? <> at entry <code style={{ fontFamily: 'var(--font-mono)' }}>{data.first_broken_id.slice(0, 8)}</code></> : ''}.
      </>,
    );
  }

  return strip(
    'var(--color-status-green)',
    'var(--color-status-green-bg)',
    <Badge variant="success" dot>Chain verified</Badge>,
    <>
      Append-only ledger intact{data.count ? <> · {data.count.toLocaleString()} entries</> : ''}.
    </>,
  );
}
